import { Checkbox } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  ApiButton,
  Button,
  Chip,
  CustomAutocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ErrorInfo,
  Typography,
  useSnackbarHandler,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import { useGetDropdownMedicalConditions } from '../../../../api-refactored/candidate/useGetDropdownMedicalConditions';
import { MedicalCondition } from '../../../../api-refactored/candidate/useGetEnabledMedicalConditions';
import { usePostIexamMedicalCondition } from '../../../../api-refactored/candidate/usePostIexamMedicalCondition';
import { usePutEnableMedicalCondition } from '../../../../api-refactored/candidate/usePutEnableMedicalCondition';
import {
  GET_DROPDOWN_MEDICAL_CONDITIONS_QUERY_KEY,
  GET_ENABLED_MEDICAL_CONDITIONS_QUERY_KEY,
} from '../../../../constants/queryKeys';
import { useStyles } from './style';

interface AddMedicalConditionModalProps {
  modalState: boolean;
  setModalState: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddMedicalConditionModal = ({ modalState, setModalState }: AddMedicalConditionModalProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const queryClient = useQueryClient();
  const [mutateError, setMutateError] = useState<string>();
  const [selectedMedicalCondition, setSelectedMedicalCondition] = useState<MedicalCondition[]>([]);

  const addMedicalCondition = usePutEnableMedicalCondition({
    onSuccess: () => {
      queryClient.invalidateQueries(GET_DROPDOWN_MEDICAL_CONDITIONS_QUERY_KEY);
      queryClient.invalidateQueries(GET_ENABLED_MEDICAL_CONDITIONS_QUERY_KEY);
      setSelectedMedicalCondition([]);
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>AccessArrangementConfiguration.MedicalConditionListUpdated'),
      });
    },
    onError: () => {
      setMutateError(t('admin->>AccessArrangementConfiguration.UnableToAddMedicalCondition'));
    },
  });

  const iexamMedicalCondition = usePostIexamMedicalCondition({
    onSuccess: () => {
      queryClient.invalidateQueries(GET_DROPDOWN_MEDICAL_CONDITIONS_QUERY_KEY);
      // to reset the selection cos data might updated
      setSelectedMedicalCondition([]);
    },
  });

  const {
    data: dropdownData,
    isLoading: isDropdownDataLoading,
    error: isDropdownDataError,
    refetch: refetchDropdownData,
  } = useGetDropdownMedicalConditions();

  const onDialogClose = () => {
    setModalState(false);
    setSelectedMedicalCondition([]);
    iexamMedicalCondition.reset();
    addMedicalCondition.reset();
    setMutateError(undefined);
  };

  const onSubmitConfirm = () => {
    setMutateError(undefined);
    addMedicalCondition.mutate(
      selectedMedicalCondition.map((option: MedicalCondition) => ({
        uuid: option.uuid,
      }))
    );
  };

  return (
    <Dialog open={modalState} maxWidth="md">
      <DialogTitle>{t('admin->>AccessArrangementConfiguration.AddMedicalCondition')}</DialogTitle>

      <DialogContent>
        <Typography className={classes.retrieveInstruction}>
          {t('admin->>AccessArrangementConfiguration.RetrieveMcListInstruction')}
        </Typography>
        <ApiButton
          className={classes.retrieveButton}
          onClick={() => {
            iexamMedicalCondition.mutate();
          }}
          status={iexamMedicalCondition.status}
          successLabel={t('admin->>AccessArrangementConfiguration.MedicalConditionUpdated')}
          variant="outlined"
          id="button-retrieve-latest-medical-condition"
        >
          {t('admin->>AccessArrangementConfiguration.RetrieveMcList')}
        </ApiButton>

        <CustomAutocomplete<MedicalCondition, true, false, undefined>
          onChange={(event, value) => setSelectedMedicalCondition(value as MedicalCondition[])}
          options={dropdownData ?? []}
          label={t('admin->>AccessArrangementConfiguration.SelectMedicalCondition')}
          loading={isDropdownDataLoading}
          loadingError={!!isDropdownDataError}
          loadingErrorOnReFetch={refetchDropdownData}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
          placeholder={t('admin->>AccessArrangementConfiguration.SelectMedicalCondition')}
          multiple
          id="select-medical-condition"
          disableCloseOnSelect
          value={selectedMedicalCondition}
          disabled={addMedicalCondition.isLoading}
          renderOption={(_,option,state,__) => (
            <>
              <Checkbox checked={state.selected} id={`autocomplete-option-${option.name}`} />
              {option.name}
            </>
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, tagIndex) => (
              <Chip id="auto-complete" label={option.name} {...getTagProps({ index: tagIndex })} />
            ))
          }
        />
        {mutateError && <ErrorInfo label={mutateError} />}
      </DialogContent>

      <DialogActions>
        <div className={classes.dialogButtonContainer}>
          <Button type="button" variant="text" id="button-cancel" onClick={() => onDialogClose()}>
            {t('admin->>AccessArrangementConfiguration.Cancel')}
          </Button>
          <ApiButton
            type="submit"
            onClick={() => {
              onSubmitConfirm();
            }}
            id="button-add-medical-condition"
            status={addMedicalCondition.status}
            disabled={selectedMedicalCondition.length === 0}
            successLabel={t('admin->>AccessArrangementConfiguration.AddedToList')}
          >
            <IconWithLabel
              iconSize="M"
              iconName={IconName.add}
              label={t('admin->>AccessArrangementConfiguration.AddMedicalConditionButton', {
                number:
                  selectedMedicalCondition.length > 0 ? `${selectedMedicalCondition.length} ` : '',
              })}
              typographyProps={{
                variant: 'h5',
              }}
            />
          </ApiButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default AddMedicalConditionModal;
